import React from "react"
import cx from "classnames"
import { useRender } from "@base-ui/react/use-render"
import { matchesExactPath } from "../navigation/navigation-utils"
import { CountBadge } from "../count-badge"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import { usePageHeaderContext } from "./page-header-context"

// =============================================================================
// Props
// =============================================================================

export type PageHeaderTabLinkProps = {
  children?: React.ReactNode
  className?: string
  /**
   * Optional count rendered in a `CountBadge` after `children`.
   *
   * Composed alongside `children`, so it only renders when the label is passed
   * via `children` (see `render`).
   */
  count?: number
  /**
   * Tab link href. The tab link is active when `currentPath.startsWith(href)`.
   */
  href: string
  /**
   * Optional icon rendered before `children`.
   *
   * Composed alongside `children`, so it only renders when the label is passed
   * via `children` (see `render`).
   */
  icon?: IconName
  /**
   * Replace the rendered element so consumers can supply their own link
   * component (e.g. Inertia `Link`, react-router `Link`, plain `<a>`).
   * Defaults to a `<a>` when omitted.
   *
   * The label must be passed as `children` of the tab link, NOT as children of
   * the render element — pass a childless element (`render={<Link href="…" />}`)
   * and let the tab link compose the content. Children placed directly on the
   * render element (`render={<Link href="…">Forms</Link>}`) take precedence over
   * the tab link's composed content, dropping `icon` and `count`.
   */
  render?: useRender.RenderProp
}

// =============================================================================
// Styles
// =============================================================================

const pageHeaderTabLinkStyles = [
  "outline-2 -outline-offset-2 outline-transparent",
  "border-b border-transparent aria-[current]:border-gray-900",
  "inline-flex items-center gap-x-2",
  "text-sm leading-6 font-semibold text-gray-700 text-nowrap aria-[current]:text-gray-900",
  "transition-colors duration-200",
  "hover:border-gray-700 aria-[current]:hover:border-gray-900",
  "focus-visible:outline-gray-900"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Shared base for page header tab links. Not part of the public Page Header
 * API — use `PagePrimaryHeader.TabLink` or `PageSecondaryHeader.TabLink`.
 *
 * Renders an `<a>` with `aria-current="page"` when `currentPath` starts with `href`.
 */
export function PageHeaderTabLink({
  className,
  children,
  count,
  href,
  icon,
  render
}: PageHeaderTabLinkProps) {
  const { currentPath } = usePageHeaderContext()
  const isActive = matchesExactPath(currentPath, href)

  return useRender({
    defaultTagName: "a",
    render,
    props: {
      "aria-current": isActive ? ("page" as const) : undefined,
      children: (
        <>
          {icon && <Icon name={icon} size="xs" />}
          {children}
          {count !== undefined && <CountBadge count={count} />}
        </>
      ),
      className: cx(pageHeaderTabLinkStyles, className),
      href
    }
  })
}
